import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs from 'dayjs'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { postSPDataWithParams } from 'src/supportPortal/services/services'

const RescheduleSlotDialog = ({ open, onClose, bookedSlot, refreshBookedSlots }) => {
  const [newDateTime, setNewDateTime] = useState(dayjs())
  const [reason, setReason] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleReschedule = async () => {
    if (!newDateTime) {
      toast.error('Please select a new date and time')
      return
    }

    if (!reason.trim()) {
      toast.error('Please enter a reason for rescheduling')
      return
    }

    setIsLoading(true)
    const loadingToast = toast.loading('Rescheduling appointment...')

    try {
      const resp = await postSPDataWithParams(
        '/api/secure/clinic-assistant/booked-slots/reschedule',
        {
          newStartTime: newDateTime.toISOString().slice(0, 19).replace('T', ' '),
          newPublicAccessVideoToken: bookedSlot?.publicAccessVideoToken,
          newMode: bookedSlot?.mode,
          newMentorId: bookedSlot?.mentorId,
        },
        {
          bookedEventId: bookedSlot?.id,
          sendCommunicationToClient: false,
          sendCommunicationToMentor: false,
          handlePaymentLinks: true,
          reason: reason.trim(),
        },
      )

      if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        toast.update(loadingToast, {
          render: 'Appointment rescheduled successfully',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        })
        refreshBookedSlots()
        onClose()
      } else {
        throw new Error('Failed to reschedule appointment')
      }
    } catch (error) {
      toast.update(loadingToast, {
        render: 'Failed to reschedule appointment',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setNewDateTime(dayjs())
      setReason('')
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Reschedule Appointment</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Current appointment: {new Date(bookedSlot?.startTime).toLocaleString()}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          <DateTimePicker
            label="New Date & Time"
            value={newDateTime}
            format='DD MMM YYYY HH:mm'
            onChange={(newValue) => setNewDateTime(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: 'outlined',
              },
            }}
            minDateTime={dayjs()}
          />

          <TextField
            label="Reason for Reschedule"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2 }}
            placeholder="Enter reason for rescheduling"
            required
            error={!reason.trim() && reason !== ''}
            helperText={!reason.trim() && reason !== '' ? 'Reason is required' : ''}
          />

          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Patient:</strong> {bookedSlot?.patientInfo?.name || bookedSlot?.userName}
            </Typography>
            {/* <Typography variant="body2" color="text.secondary">
              <strong>Phone:</strong> {bookedSlot?.patientInfo?.phone}
            </Typography> */}
            <Typography variant="body2" color="text.secondary">
              <strong>Event Type:</strong> {bookedSlot?.eventType}
            </Typography>
          </Box>

          {/* <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: '#fff3cd',
              borderRadius: 1,
              border: '1px solid #ffecb5',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> Patient and mentor will NOT be automatically notified about this reschedule.
            </Typography>
          </Box> */}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleReschedule}
          disabled={isLoading || !newDateTime || !reason.trim()}
          startIcon={isLoading && <CircularProgress size={16} />}
        >
          {isLoading ? 'Rescheduling...' : 'Reschedule'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RescheduleSlotDialog
